/* ============================================================
   La Nuit — availability.js
   <availability-form> — AJAX wrapper around the native Shopify
   customer form used for "Notify me when available" and
   "Request this product".

   The inner form is a real {% form 'customer' %}, so Shopify
   injects the correct action + hidden fields and creates/updates
   a tagged Customer. We intercept submit only to give an instant
   on-site confirmation (the customer is acknowledged immediately
   regardless of any merchant-side email automation).

   Plain script — reads utilities from window.LaNuit.* (no ESM
   import, matching the theme's asset strategy).
   ============================================================ */
(function () {
  class AvailabilityForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.successEl = this.querySelector('[data-availability-success]');
      this.submitBtn = this.querySelector('[data-availability-submit]');
      this.labelEl = this.querySelector('[data-availability-label]');
      this.mode = this.dataset.mode || 'notify';
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    onSubmit(e) {
      e.preventDefault();
      if (this._busy) return;

      const email = this.form.querySelector('input[type="email"]');
      if (email && !email.checkValidity()) {
        email.reportValidity();
        return;
      }

      this._busy = true;
      const originalLabel = this.labelEl ? this.labelEl.textContent : '';
      if (this.submitBtn) this.submitBtn.setAttribute('disabled', '');
      if (this.labelEl) this.labelEl.textContent = '…';

      /* Post to the store's CANONICAL host (data-action = {{ shop.url }})
         so the request never hits the apex→www (or www→apex) 301 that
         drops the POST body and breaks the response. The customer form
         is a "simple" multipart POST, so a cross-origin no-cors request
         is still delivered to and processed by Shopify — we just can't
         read the opaque response, which is fine: the lead is captured
         and we confirm optimistically (same approach Shopify reference
         themes use for the newsletter form). Only a genuine network
         failure (offline) rejects and surfaces the error. */
      var action = this.dataset.action || this.form.action;
      fetch(action, {
        method: 'POST',
        body: new FormData(this.form),
        mode: 'no-cors',
        redirect: 'follow'
      })
        .then(() => {
          this.showSuccess();
        })
        .catch((err) => {
          console.error('Availability submit failed', err);
          this._busy = false;
          if (this.submitBtn) this.submitBtn.removeAttribute('disabled');
          if (this.labelEl) this.labelEl.textContent = originalLabel;
          if (window.LaNuit && typeof window.LaNuit.toast === 'function') {
            window.LaNuit.toast(
              'Something went wrong — please try again.',
              { variant: 'error' }
            );
          }
        });
    }

    showSuccess() {
      const inner = this.querySelector('[data-availability-inner]');
      const formEl = this.form;
      if (formEl) formEl.setAttribute('hidden', '');
      // Hide everything except the success message.
      if (inner) {
        inner.querySelectorAll('.availability__title, .availability__body')
          .forEach((el) => el.setAttribute('hidden', ''));
      }
      if (this.successEl) this.successEl.removeAttribute('hidden');

      const msg = this.mode === 'request'
        ? 'Request received — we’ll be in touch soon.'
        : 'You’re on the list — we’ll email you the moment it’s back.';
      if (window.LaNuit && typeof window.LaNuit.toast === 'function') {
        window.LaNuit.toast(msg, { variant: 'success' });
      }
      this.dispatchEvent(new CustomEvent('availability:submitted', {
        bubbles: true,
        detail: { mode: this.mode }
      }));
    }
  }

  if (!customElements.get('availability-form')) {
    customElements.define('availability-form', AvailabilityForm);
  }
})();
